import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import DonutChart from '..';
import { formatDateTimeDisplay } from '../../../../utils/base';

const CalendarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'enableScroll',
})(({ enableScroll }) => ({
    display: 'flex',
    alignItems: 'flex-start', // 위쪽 정렬로 하단에 그림자 여백 확보
    width: '100%',
    position: 'relative',
    overflow: 'visible', // 선택된 날짜 bg/그림자가 잘리지 않도록 항상 visible
    justifyContent: 'center',
    height: '110px',
    minHeight: '110px',
    maxHeight: '110px',
}));

const StyledWeekCalendar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'enableScroll' && prop !== 'enableWeekSwipe' && prop !== 'size',
})(({ enableScroll, enableWeekSwipe, size }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: enableScroll ? 'flex-start' : 'center',
    padding: enableScroll ? '0' : '0 10px', // 스크롤 모드일 때 패딩 제거
    width: '100%',
    maxWidth: '100%',
    gap: enableScroll && size === 'medium' ? '3px' : '5px', // medium 스크롤 모드일 때 gap 줄임
    overflow: enableScroll ? 'visible' : 'visible', // 스크롤 모드일 때도 visible로 설정 (sx prop에서 overflowX: 'auto'로 덮어씀)
    boxSizing: 'border-box',
    position: 'relative', // 터치 이벤트가 제대로 작동하도록 position 추가
    zIndex: 1, // 다른 요소 위에 표시되도록 z-index 추가
    // enableWeekSwipe와 관계없이 항상 pointerEvents를 auto로 설정
    pointerEvents: 'auto',
    ...(enableWeekSwipe && {
        touchAction: 'none', // 터치 이벤트 수동 처리
    }),
}));

const DayItem = styled(Box, {
    shouldForwardProp: (prop) => {
        const excludedProps = ['isSelected', 'selectedBackgroundColor', 'size', 'enableScroll', 'enableWeekSwipe'];
        return !excludedProps.includes(prop);
    },
})(({ theme, isSelected, size, enableScroll, enableWeekSwipe, selectedBackgroundColor = theme.palette.figma.block }) => ({
    flex: enableScroll ? 'none' : 1,
    width: enableScroll ? (size === 'large' ? '100px' : '45px') : 'auto', // large: 100px, medium: 45px (7개가 한 화면에 보이도록)
    display: 'flex',
    flexDirection: 'column',
    gap: size === 'large' ? '0px' : '10px', // large일 때 간격 줄임
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: size === 'large' ? '5px 0' : '10px 0', // large일 때 패딩 줄임
    borderRadius: isSelected ? '10px' : '15px',
    backgroundColor: isSelected ? selectedBackgroundColor : 'transparent',
    boxShadow: isSelected ? (theme.palette.shadows?.elevation?.[2] ?? 'none') : 'none',
    minHeight: 0,
    minWidth: 0,
    flexShrink: 0,
    // 항상 pointerEvents를 auto로 설정하여 클릭 이벤트가 정상적으로 작동하도록 함
    pointerEvents: 'auto',
    position: 'relative', // z-index를 위해 필요
    zIndex: 1, // 다른 요소 위에 표시
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    ...(enableWeekSwipe && {
        touchAction: 'pan-y', // 수직 스크롤만 허용, 수평 스와이프는 수동 처리
    }),
}));

const DayChart = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'size' && prop !== 'isSelected',
})(({ size, isSelected }) => ({
    width: size === 'large' ? '80px' : '35px',
    height: size === 'large' ? '80px' : '35px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    flexShrink: 0,
    maxWidth: '100%',
    pointerEvents: 'none', // 터치 이벤트가 부모로 전파되도록 (차트는 클릭 불가)
}));

const WeekCalendar = ({
    days = [],
    onDayClick,
    sx = {},
    enableScroll = false,
    allDaysData = [], // 과거 30일치 데이터
    isLoading = false, // 데이터 로딩 상태
    size = 'medium', // 차트 크기: 'medium', 'large'
    selectedBackgroundColor,
    enableWeekSwipe = false, // 주단위 스와이프 활성화
    onWeekChange, // 주 변경 콜백 (delta: -1 또는 1)
    selectedDate, // 현재 선택된 날짜 (주 변경 시 사용)
    onScrollToEnd = null, // 스크롤 끝에서 콜백 함수 (direction: 'past' 또는 'future')
    fullWidth = false, // true면 스크롤 영역이 화면 좌우 끝까지 (maxWidth 100%)
    ...props
}) => {
    const theme = useTheme();
    const calendarRef = useRef(null);
    const hasInitializedScroll = useRef(false); // 초기 스크롤 설정 여부 추적

    // 드래그 스크롤은 제거하고 기본 스크롤 사용

    // 스크롤 모드에서 마우스 드래그로 스크롤 (텍스트 선택 방지)
    const dragScrollRef = useRef({
        isDown: false,
        startX: 0,
        startScrollLeft: 0,
        moved: false,
    });
    const [isDragScrolling, setIsDragScrolling] = useState(false);

    const clearTextSelection = useCallback(() => {
        const selection = window.getSelection?.();
        if (selection && selection.type !== 'None') {
            selection.removeAllRanges();
        }
    }, []);

    const handleDragScrollMouseDown = useCallback((e) => {
        if (!enableScroll) return;
        // 좌클릭만
        if (e.button !== 0) return;
        if (!calendarRef.current) return;

        dragScrollRef.current.isDown = true;
        dragScrollRef.current.startX = e.clientX;
        dragScrollRef.current.startScrollLeft = calendarRef.current.scrollLeft;
        dragScrollRef.current.moved = false;
        setIsDragScrolling(true);

        // 드래그 시작 시 텍스트 선택 방지
        if (e.cancelable) e.preventDefault();
        clearTextSelection();
    }, [enableScroll, clearTextSelection]);

    const handleDragScrollMouseMove = useCallback((e) => {
        if (!enableScroll) return;
        if (!dragScrollRef.current.isDown) return;
        if (!calendarRef.current) return;

        const dx = e.clientX - dragScrollRef.current.startX;
        if (Math.abs(dx) > 3) {
            dragScrollRef.current.moved = true;
        }

        calendarRef.current.scrollLeft = dragScrollRef.current.startScrollLeft - dx;
        clearTextSelection();
        if (e.cancelable) e.preventDefault();
    }, [enableScroll, clearTextSelection]);

    const handleDragScrollMouseUp = useCallback(() => {
        if (!dragScrollRef.current.isDown) return;
        dragScrollRef.current.isDown = false;
        setIsDragScrolling(false);
    }, []);

    useEffect(() => {
        if (!enableScroll) return;
        if (!isDragScrolling) return;

        const onMove = (e) => handleDragScrollMouseMove(e);
        const onUp = () => handleDragScrollMouseUp();

        // passive: false 로 preventDefault 허용
        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('mouseup', onUp);

        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [enableScroll, isDragScrolling, handleDragScrollMouseMove, handleDragScrollMouseUp]);

    // 주단위 스와이프를 위한 ref
    const swipeRef = useRef({
        startX: 0,
        startY: 0,
        endX: 0,
        endY: 0,
        isVerticalScroll: false,
        hasMoved: false,
        isClick: false,
        isMouseDown: false, // 마우스 드래그 상태
        touchStartX: 0,
        touchStartY: 0,
        touchStartTime: 0
    });

    const handleDayClick = useCallback((date, index, event) => {
        // 클릭 발생 플래그 설정하여 스와이프 처리 방지
        if (enableWeekSwipe) {
            swipeRef.current.isClick = true;
            setTimeout(() => {
                swipeRef.current.isClick = false;
            }, 150);
        }
        if (onDayClick && date) {
            onDayClick(date, index);
        } else {
            console.log('onDayClick not called:', { onDayClick: !!onDayClick, date: !!date });
        }
    }, [onDayClick, enableWeekSwipe]);

    // 드래그 스크롤은 제거하고 기본 스크롤 사용

    // 스크롤 이벤트 핸들러
    const handleScroll = (event) => {
        if (!enableScroll || isLoading) return;

        const container = event.target;
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        // 왼쪽 끝에 도달했을 때 (과거 데이터 로드)
        if (scrollLeft <= 5 && onScrollToEnd) {
            onScrollToEnd('past');
        }

        // 오른쪽 끝에 도달했을 때 (미래 데이터 로드)
        if (scrollLeft + clientWidth >= scrollWidth - 5 && onScrollToEnd) {
            onScrollToEnd('future');
        }
    };

    // 초기 스크롤 위치 설정 - 오늘 데이터가 화면에 보이도록 (최초 마운트 시에만)
    useLayoutEffect(() => {
        // 초기 스크롤이 이미 설정되었거나, 스크롤이 비활성화되어 있으면 실행하지 않음
        if (hasInitializedScroll.current || !enableScroll || !allDaysData || allDaysData.length === 0) {
            return;
        }

        if (calendarRef.current) {
            // 오늘 데이터가 화면에 보이도록 스크롤 위치 설정
            // 오늘이 배열의 마지막(가장 오른쪽)에 있으므로 스크롤을 맨 오른쪽으로 설정
            // 스크롤 위치를 정확히 설정
            const setScrollPosition = () => {
                if (calendarRef.current && !hasInitializedScroll.current) {
                    // 내부 Box 요소 찾기
                    const innerBox = calendarRef.current.querySelector('[data-inner-box]') || calendarRef.current.firstElementChild;
                    if (!innerBox) return;

                    // 실제 스크롤 가능한 너비와 컨테이너 너비를 정확히 계산
                    const scrollWidth = calendarRef.current.scrollWidth;
                    const clientWidth = calendarRef.current.clientWidth;

                    // 오른쪽 여백을 최소화하기 위해 정확히 오른쪽 끝으로 스크롤
                    // scrollWidth가 clientWidth보다 크면 스크롤 가능
                    if (scrollWidth > clientWidth) {
                        // 마지막 DayItem의 실제 위치를 계산
                        const dayItems = innerBox.querySelectorAll('[data-day-item]');
                        if (dayItems.length > 0) {
                            const lastItem = dayItems[dayItems.length - 1];
                            const lastItemRect = lastItem.getBoundingClientRect();

                            // 내부 Box의 너비를 마지막 아이템의 실제 오른쪽 끝에 약간의 여백을 두고 조정
                            const innerBoxRect = innerBox.getBoundingClientRect();
                            const lastItemRightRelativeToInnerBox = lastItemRect.right - innerBoxRect.left;
                            const paddingRight = 20; // 우측 여백 추가

                            // 내부 Box의 너비를 마지막 아이템의 오른쪽 끝 + 여백으로 설정
                            if (innerBox.style && lastItemRightRelativeToInnerBox > 0) {
                                const boxWidth = lastItemRightRelativeToInnerBox + paddingRight;
                                innerBox.style.width = `${boxWidth}px`;
                                innerBox.style.minWidth = `${boxWidth}px`;
                                innerBox.style.maxWidth = `${boxWidth}px`;
                            }

                            // 내부 Box 너비 조정 후 스크롤 위치 재계산
                            requestAnimationFrame(() => {
                                if (calendarRef.current) {
                                    const newScrollWidth = calendarRef.current.scrollWidth;
                                    const newClientWidth = calendarRef.current.clientWidth;
                                    if (newScrollWidth > newClientWidth) {
                                        // 최대한 오른쪽으로 스크롤
                                        calendarRef.current.scrollLeft = newScrollWidth - newClientWidth;
                                    }
                                }
                            });
                        } else {
                            // DayItem을 찾을 수 없으면 기본 계산 사용
                            const maxScroll = scrollWidth - clientWidth;
                            calendarRef.current.scrollLeft = maxScroll;
                        }

                        hasInitializedScroll.current = true; // 초기 스크롤 설정 완료
                    } else {
                        calendarRef.current.scrollLeft = 0;
                        hasInitializedScroll.current = true; // 초기 스크롤 설정 완료
                    }
                }
            };

            // 즉시 설정
            setScrollPosition();

            // DOM이 완전히 렌더링된 후 다시 설정
            setTimeout(() => {
                setScrollPosition();
                // 추가로 내부 Box 너비를 마지막 아이템에 맞게 조정 (약간의 여백 포함)
                const innerBox = calendarRef.current?.querySelector('[data-inner-box]') || calendarRef.current?.firstElementChild;
                if (innerBox) {
                    const dayItems = innerBox.querySelectorAll('[data-day-item]');
                    if (dayItems.length > 0) {
                        const lastItem = dayItems[dayItems.length - 1];
                        const lastItemRect = lastItem.getBoundingClientRect();
                        const innerBoxRect = innerBox.getBoundingClientRect();
                        // 마지막 아이템의 오른쪽 끝까지의 실제 너비 계산 + 여백
                        const paddingRight = 20; // 우측 여백
                        const exactWidth = (lastItemRect.right - innerBoxRect.left) + paddingRight;
                        if (innerBox.style && exactWidth > 0) {
                            innerBox.style.width = `${exactWidth}px`;
                            innerBox.style.minWidth = `${exactWidth}px`;
                            innerBox.style.maxWidth = `${exactWidth}px`;
                        }
                        // 스크롤 위치 재조정
                        if (calendarRef.current) {
                            const scrollWidth = calendarRef.current.scrollWidth;
                            const clientWidth = calendarRef.current.clientWidth;
                            if (scrollWidth > clientWidth) {
                                calendarRef.current.scrollLeft = scrollWidth - clientWidth;
                            }
                        }
                    }
                }
            }, 50);
            setTimeout(setScrollPosition, 200);
            setTimeout(setScrollPosition, 500);
        }
    }, [enableScroll, allDaysData, size]);

    // 주단위 스와이프를 위한 터치 핸들러 - useCallback으로 메모이제이션
    const handleTouchStart = useCallback((e) => {
        if (!enableWeekSwipe) {
            return;
        }

        e.stopPropagation(); // 이벤트 전파 중지
        const touch = e.touches[0];
        if (!touch) {
            console.log('No touch found');
            return;
        }

        swipeRef.current.startX = touch.clientX;
        swipeRef.current.startY = touch.clientY;
        swipeRef.current.endX = touch.clientX;
        swipeRef.current.endY = touch.clientY;
        swipeRef.current.isVerticalScroll = false;
        swipeRef.current.hasMoved = false;
        swipeRef.current.isClick = false;
    }, [enableWeekSwipe]);

    const handleTouchMove = useCallback((e) => {
        if (!enableWeekSwipe) return;

        e.stopPropagation(); // 이벤트 전파 중지
        const touch = e.touches[0];
        if (!touch) {
            console.log('No touch found in move');
            return;
        }
        const deltaX = Math.abs(touch.clientX - swipeRef.current.startX);
        const deltaY = Math.abs(touch.clientY - swipeRef.current.startY);

        // 수평 이동이 수직 이동보다 크면 스와이프로 간주하고 기본 동작 방지
        if (deltaX > deltaY && deltaX > 10) {
            e.preventDefault();
        }

        swipeRef.current.endX = touch.clientX;
        swipeRef.current.endY = touch.clientY;
        swipeRef.current.hasMoved = true;

        // 수직 이동이 수평 이동보다 크면 스크롤로 간주
        if (deltaY > deltaX && deltaY > 10) {
            swipeRef.current.isVerticalScroll = true;
        }
    }, [enableWeekSwipe]);

    const handleTouchEnd = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChange || !selectedDate) {
            return;
        }

        // 터치 종료 시점의 좌표 업데이트
        if (e && e.changedTouches && e.changedTouches[0]) {
            swipeRef.current.endX = e.changedTouches[0].clientX;
            swipeRef.current.endY = e.changedTouches[0].clientY;
        }

        // 짧은 지연을 두어 클릭 이벤트가 먼저 처리되도록 함
        setTimeout(() => {
            // 클릭이 발생했으면 스와이프 처리 무시
            if (swipeRef.current.isClick) {
                console.log('handleTouchEnd: isClick is true, returning');
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
                return;
            }

            // isVerticalScroll은 중간에 설정될 수 있으므로, 최종 이동 거리로 다시 확인
            const finalDeltaX = Math.abs(swipeRef.current.endX - swipeRef.current.startX);
            const finalDeltaY = Math.abs(swipeRef.current.endY - swipeRef.current.startY);

            // 최종 이동 거리 기준으로 세로 스크롤인지 확인
            if (swipeRef.current.isVerticalScroll && finalDeltaY >= finalDeltaX * 0.8) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
                return;
            } else if (swipeRef.current.isVerticalScroll) {
                // 중간에 세로 스크롤로 감지되었지만 최종적으로는 가로 스와이프인 경우                
                swipeRef.current.isVerticalScroll = false;
            }

            // 이동하지 않았으면 클릭으로 간주
            if (!swipeRef.current.hasMoved) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
                return;
            }

            const deltaX = swipeRef.current.endX - swipeRef.current.startX;
            const deltaY = swipeRef.current.endY - swipeRef.current.startY;
            const threshold = 50;
            const clickThreshold = 10;

            // 클릭으로 간주
            if (Math.abs(deltaX) < clickThreshold && Math.abs(deltaY) < clickThreshold) {
                console.log('handleTouchEnd: Click threshold not met, returning');
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
                return;
            }

            // 수직 이동이 수평 이동보다 크거나 같으면 스크롤로 간주하고 주간 이동하지 않음
            // deltaY가 deltaX의 0.8배 이상이면 세로 스와이프로 간주
            if (Math.abs(deltaY) >= Math.abs(deltaX) * 0.8) {
                console.log('Vertical scroll detected, ignoring swipe', { deltaX, deltaY, ratio: Math.abs(deltaY) / Math.abs(deltaX) });
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false, touchStartX: 0, touchStartY: 0, touchStartTime: 0 };
                return;
            }

            // 수평 이동이 수직 이동보다 명확히 크고 threshold보다 크면 스와이프로 간주
            // deltaX가 deltaY의 1.2배 이상이고 threshold보다 크면 가로 스와이프
            if (Math.abs(deltaX) > Math.abs(deltaY) * 1.2 && Math.abs(deltaX) > threshold) {
                console.log('Horizontal swipe detected:', { deltaX, deltaY, ratio: Math.abs(deltaX) / Math.abs(deltaY) });
                if (deltaX > 0) {
                    // 오른쪽 스와이프 (이전 주로 이동, 즉 과거로 이동)
                    // 항상 7일 전으로 이동 (제한 없음)
                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() - 7);
                    newDate.setHours(0, 0, 0, 0);
                    onWeekChange(newDate, -1);
                } else {
                    // 왼쪽 스와이프 (다음 주로 이동, 즉 미래로 이동)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    // 다음 주로 이동
                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() + 7);
                    newDate.setHours(0, 0, 0, 0);

                    // 다음 주의 시작일 계산
                    const nextDay = newDate.getDay();
                    const startOfNextWeek = new Date(newDate);
                    startOfNextWeek.setDate(newDate.getDate() - nextDay);
                    startOfNextWeek.setHours(0, 0, 0, 0);

                    // 이번 주의 시작일 계산
                    const todayDay = today.getDay();
                    const startOfThisWeek = new Date(today);
                    startOfThisWeek.setDate(today.getDate() - todayDay);
                    startOfThisWeek.setHours(0, 0, 0, 0);

                    const nextWeekTime = startOfNextWeek.getTime();
                    const thisWeekTime = startOfThisWeek.getTime();

                    // 다음 주가 이번 주보다 미래가 아니면 이동 가능
                    if (nextWeekTime <= thisWeekTime) {
                        onWeekChange(newDate, 1);
                    }
                }
            }

            // 리셋
            swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
        }, 50);
    }, [enableWeekSwipe, onWeekChange, selectedDate]);

    // 마우스 드래그 이벤트 핸들러 (데스크톱용 - 주간 스와이프)
    const handleWeekSwipeMouseDown = useCallback((e) => {
        console.log('handleWeekSwipeMouseDown called:', { enableWeekSwipe, onWeekChange: !!onWeekChange, selectedDate: !!selectedDate, target: e.target });
        if (!enableWeekSwipe || !onWeekChange || !selectedDate) return;

        // DayItem에서 발생한 클릭인지 확인
        const clickableElement = e.target.closest('[data-day-clickable="true"]');
        if (clickableElement) {
            console.log('DayItem detected, skipping drag');
            // DayItem에서 발생한 클릭은 드래그 처리하지 않음
            // 이벤트를 소비하지 않고 그대로 전파되도록 함
            return; // 아무것도 하지 않고 이벤트가 자식으로 전파되도록 함
        }

        console.log('Starting drag');
        // DayItem이 아닌 곳에서 발생한 경우에만 드래그 처리
        // preventDefault는 호출하지 않음 (클릭 이벤트 방해 방지)
        swipeRef.current.isMouseDown = true;
        swipeRef.current.startX = e.clientX;
        swipeRef.current.startY = e.clientY;
        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.hasMoved = false;
        swipeRef.current.isClick = false;
    }, [enableWeekSwipe, onWeekChange, selectedDate]);

    const handleWeekSwipeMouseMove = useCallback((e) => {
        if (!enableWeekSwipe || !swipeRef.current.isMouseDown) return;
        console.log('Mouse move:', { x: e.clientX, y: e.clientY });
        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.hasMoved = true;
    }, [enableWeekSwipe]);

    const handleWeekSwipeMouseUp = useCallback((e) => {
        if (!enableWeekSwipe || !onWeekChange || !selectedDate || !swipeRef.current.isMouseDown) {
            swipeRef.current.isMouseDown = false;
            return;
        }

        swipeRef.current.endX = e.clientX;
        swipeRef.current.endY = e.clientY;
        swipeRef.current.isMouseDown = false;

        // 짧은 지연을 두어 클릭 이벤트가 먼저 처리되도록 함
        setTimeout(() => {
            // 클릭이 발생했으면 스와이프 처리 무시
            if (swipeRef.current.isClick) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
                return;
            }

            // 이동하지 않았으면 클릭으로 간주
            if (!swipeRef.current.hasMoved) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
                return;
            }

            const deltaX = swipeRef.current.endX - swipeRef.current.startX;
            const deltaY = Math.abs(swipeRef.current.endY - swipeRef.current.startY);
            const threshold = 50;
            const clickThreshold = 10;

            console.log('Mouse up check:', { deltaX, deltaY, isClick: swipeRef.current.isClick, hasMoved: swipeRef.current.hasMoved });

            // 클릭으로 간주
            if (Math.abs(deltaX) < clickThreshold && deltaY < clickThreshold) {
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
                return;
            }

            // 수직 이동이 수평 이동보다 크거나 같으면 스크롤로 간주하고 주간 이동하지 않음
            // deltaY가 deltaX의 0.8배 이상이면 세로 스와이프로 간주
            if (deltaY >= Math.abs(deltaX) * 0.8) {
                console.log('Vertical scroll detected in mouse, ignoring swipe', { deltaX, deltaY, ratio: deltaY / Math.abs(deltaX) });
                swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
                return;
            }

            // 수평 이동이 수직 이동보다 명확히 크고 threshold보다 크면 스와이프로 간주
            // deltaX가 deltaY의 1.2배 이상이고 threshold보다 크면 가로 스와이프
            if (Math.abs(deltaX) > deltaY * 1.2 && Math.abs(deltaX) > threshold) {
                console.log('Horizontal swipe detected in mouse:', { deltaX, deltaY, ratio: Math.abs(deltaX) / deltaY });
                if (deltaX > 0) {
                    // 오른쪽 드래그 (이전 주로 이동, 즉 과거로 이동)
                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() - 7);
                    newDate.setHours(0, 0, 0, 0);
                    onWeekChange(newDate, -1);
                } else {
                    // 왼쪽 드래그 (다음 주로 이동, 즉 미래로 이동)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() + 7);
                    newDate.setHours(0, 0, 0, 0);

                    // 다음 주의 시작일 계산
                    const nextDay = newDate.getDay();
                    const startOfNextWeek = new Date(newDate);
                    startOfNextWeek.setDate(newDate.getDate() - nextDay);
                    startOfNextWeek.setHours(0, 0, 0, 0);

                    // 이번 주의 시작일 계산
                    const todayDay = today.getDay();
                    const startOfThisWeek = new Date(today);
                    startOfThisWeek.setDate(today.getDate() - todayDay);
                    startOfThisWeek.setHours(0, 0, 0, 0);

                    const nextWeekTime = startOfNextWeek.getTime();
                    const thisWeekTime = startOfThisWeek.getTime();

                    // 다음 주가 이번 주보다 미래가 아니면 이동 가능
                    if (nextWeekTime <= thisWeekTime) {
                        onWeekChange(newDate, 1);
                    }
                }
            }

            // 리셋
            swipeRef.current = { startX: 0, startY: 0, endX: 0, endY: 0, isVerticalScroll: false, hasMoved: false, isClick: false, isMouseDown: false };
        }, 50);
    }, [enableWeekSwipe, onWeekChange, selectedDate]);

    // 마우스 휠 이벤트 핸들러
    const handleWheel = (e) => {
        if (!enableWeekSwipe || !onWeekChange || !selectedDate) return;

        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            // 수평 스크롤
            e.preventDefault();
            const threshold = 50;

            if (e.deltaX > threshold) {
                // 오른쪽 스크롤 (이전 주로 이동, 즉 과거로 이동)
                const newDate = new Date(selectedDate);
                newDate.setDate(selectedDate.getDate() - 7);
                newDate.setHours(0, 0, 0, 0);
                onWeekChange(newDate, -1);
            } else if (e.deltaX < -threshold) {
                // 왼쪽 스크롤 (다음 주로 이동)
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const newDate = new Date(selectedDate);
                newDate.setDate(selectedDate.getDate() + 7);
                newDate.setHours(0, 0, 0, 0);

                // 다음 주의 시작일 계산
                const nextDay = newDate.getDay();
                const startOfNextWeek = new Date(newDate);
                startOfNextWeek.setDate(newDate.getDate() - nextDay);
                startOfNextWeek.setHours(0, 0, 0, 0);

                // 이번 주의 시작일 계산
                const todayDay = today.getDay();
                const startOfThisWeek = new Date(today);
                startOfThisWeek.setDate(today.getDate() - todayDay);
                startOfThisWeek.setHours(0, 0, 0, 0);

                const nextWeekTime = startOfNextWeek.getTime();
                const thisWeekTime = startOfThisWeek.getTime();

                // 다음 주가 이번 주보다 미래가 아니면 이동 가능
                if (nextWeekTime <= thisWeekTime) {
                    onWeekChange(newDate, 1);
                }
            }
        }
    };

    // 전역 마우스 이벤트 처리 (주간 스와이프만)
    useEffect(() => {
        if (!enableWeekSwipe) return;

        const handleGlobalMouseMove = (e) => {
            if (swipeRef.current.isMouseDown && enableWeekSwipe) {
                // 주간 스와이프를 위한 마우스 이동 처리
                handleWeekSwipeMouseMove(e);
            }
        };

        const handleGlobalMouseUp = (e) => {
            if (swipeRef.current.isMouseDown && enableWeekSwipe) {
                // 주간 스와이프를 위한 마우스 업 처리
                handleWeekSwipeMouseUp(e);
            }
        };

        // 항상 이벤트 리스너를 추가하고 내부에서 조건 확인
        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('mouseup', handleGlobalMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [enableWeekSwipe, handleWeekSwipeMouseMove, handleWeekSwipeMouseUp]);


    // 직접 이벤트 리스너 추가 (useEffect) - enableWeekSwipe가 true이고 enableScroll이 false일 때만
    useEffect(() => {
        if (!enableWeekSwipe || enableScroll) {
            return;
        }

        // calendarRef가 준비될 때까지 대기
        const setupEventListeners = () => {
            if (!calendarRef.current) {
                console.log('calendarRef가 아직 준비되지 않음, 100ms 후 재시도');
                setTimeout(setupEventListeners, 100);
                return;
            }

            const element = calendarRef.current;

            const touchStartHandler = (e) => {
                console.log('touchStartHandler called:', { target: e.target.tagName });
                // 이벤트가 element 내부에서 발생했는지 확인
                const target = e.target;
                if (!element.contains(target)) {
                    console.log('touchStartHandler: target not in element');
                    return;
                }

                // DayItem 클릭 가능 여부 확인 (data-day-clickable 속성 확인)
                const clickableElement = target.closest('[data-day-clickable="true"]');
                const touch = e.touches[0];
                if (!touch) {
                    return;
                }

                if (clickableElement) {
                    console.log('touchStartHandler: DayItem detected, setting click flag and start coordinates');
                    // 클릭 가능한 요소에서 발생한 터치 이벤트는 클릭 플래그 설정
                    swipeRef.current.touchStartX = touch.clientX;
                    swipeRef.current.touchStartY = touch.clientY;
                    swipeRef.current.touchStartTime = Date.now();
                    swipeRef.current.isClick = true;
                    // DayItem에서도 스와이프를 위해 startX, startY 설정
                    swipeRef.current.startX = touch.clientX;
                    swipeRef.current.startY = touch.clientY;
                    swipeRef.current.endX = touch.clientX;
                    swipeRef.current.endY = touch.clientY;
                    swipeRef.current.isVerticalScroll = false;
                    swipeRef.current.hasMoved = false;
                    // DayItem에서 발생한 터치 이벤트는 preventDefault하지 않음 (React 이벤트가 정상 작동하도록)
                    // 하지만 stopPropagation은 하지 않음 (React 이벤트가 정상 작동하도록)
                    return;
                }

                console.log('touchStartHandler: calling handleTouchStart');
                if (e.cancelable) {
                    e.preventDefault();
                }
                handleTouchStart(e);
            };

            const touchMoveHandler = (e) => {
                // 이벤트가 element 내부에서 발생했는지 확인
                const target = e.target;
                if (!element.contains(target)) {
                    return;
                }

                const touch = e.touches[0];
                if (!touch) {
                    return;
                }

                // DayItem에서 터치가 이동하는 경우에도 endX, endY 업데이트
                const clickableElement = target.closest('[data-day-clickable="true"]');
                if (clickableElement && swipeRef.current.startX !== 0 && swipeRef.current.startY !== 0) {
                    swipeRef.current.endX = touch.clientX;
                    swipeRef.current.endY = touch.clientY;
                    swipeRef.current.hasMoved = true;
                    // 수직 이동이 수평 이동보다 크면 스크롤로 간주
                    const deltaX = Math.abs(touch.clientX - swipeRef.current.startX);
                    const deltaY = Math.abs(touch.clientY - swipeRef.current.startY);
                    if (deltaY > deltaX && deltaY > 10) {
                        swipeRef.current.isVerticalScroll = true;
                    }
                }

                if (e.cancelable) {
                    e.preventDefault();
                }
                // stopPropagation을 제거하여 전역 리스너도 이벤트를 받을 수 있도록 함
                handleTouchMove(e);
            };

            const touchEndHandler = (e) => {
                // 이벤트가 element 내부에서 발생했는지 확인
                const target = e.target;
                if (!element.contains(target)) {
                    return;
                }

                // DayItem 클릭 가능 여부 확인
                const clickableElement = target.closest('[data-day-clickable="true"]');
                if (clickableElement && swipeRef.current.isClick && swipeRef.current.touchStartTime) {
                    const touch = e.changedTouches[0];
                    if (touch) {
                        const deltaX = Math.abs(touch.clientX - swipeRef.current.touchStartX);
                        const deltaY = Math.abs(touch.clientY - swipeRef.current.touchStartY);
                        const deltaTime = Date.now() - swipeRef.current.touchStartTime;

                        console.log('Touch end on DayItem:', { deltaX, deltaY, deltaTime });

                        // 이동 거리가 작고 시간이 짧으면 클릭으로 간주
                        // 스와이프인 경우 (deltaX 또는 deltaY가 10 이상)는 클릭 처리하지 않음
                        if (deltaX < 10 && deltaY < 10 && deltaTime < 500) {
                            console.log('Touch click detected, calling handleDayClick');
                            e.stopPropagation();
                            const dateStr = clickableElement.getAttribute('data-date');
                            const index = Array.from(element.querySelectorAll('[data-day-clickable="true"]')).indexOf(clickableElement);
                            if (dateStr && onDayClick) {
                                const date = new Date(dateStr);
                                handleDayClick(date, index, e);
                            }
                            swipeRef.current.isClick = false;
                            swipeRef.current.touchStartTime = 0;
                            return;
                        } else {
                            // 스와이프인 경우 클릭 플래그를 해제하고 스와이프 처리로 넘어감
                            console.log('Touch swipe detected on DayItem, processing as swipe');
                            // 스와이프 처리를 위해 startX, startY를 설정
                            swipeRef.current.startX = swipeRef.current.touchStartX;
                            swipeRef.current.startY = swipeRef.current.touchStartY;
                            swipeRef.current.endX = touch.clientX;
                            swipeRef.current.endY = touch.clientY;
                            swipeRef.current.hasMoved = true;
                            swipeRef.current.isClick = false;
                            swipeRef.current.touchStartTime = 0;
                            // 스와이프 처리를 위해 handleTouchEnd로 넘어감
                        }
                    } else {
                        swipeRef.current.isClick = false;
                        swipeRef.current.touchStartTime = 0;
                    }
                }

                if (e.cancelable) {
                    e.preventDefault();
                }
                // stopPropagation을 제거하여 전역 리스너도 이벤트를 받을 수 있도록 함
                handleTouchEnd(e);
            };

            // capture phase에서 이벤트를 먼저 잡아서 처리
            // capture: true로 설정하여 자식 요소의 이벤트보다 먼저 실행되도록 함
            // 하지만 DayItem에서는 이벤트를 처리하지 않도록 함
            element.addEventListener('touchstart', touchStartHandler, { passive: false, capture: true });
            element.addEventListener('touchmove', touchMoveHandler, { passive: false, capture: true });
            element.addEventListener('touchend', touchEndHandler, { passive: false, capture: true });

            // cleanup 함수를 위해 핸들러를 저장
            return () => {
                element.removeEventListener('touchstart', touchStartHandler, { capture: true });
                element.removeEventListener('touchmove', touchMoveHandler, { capture: true });
                element.removeEventListener('touchend', touchEndHandler, { capture: true });
            };
        };

        // 이벤트 리스너 설정 시작
        const cleanupElement = setupEventListeners();

        return () => {
            if (cleanupElement) {
                cleanupElement();
            }
        };
    }, [enableWeekSwipe, enableScroll, handleTouchStart, handleTouchMove, handleTouchEnd, size, onWeekChange, selectedDate, onDayClick, handleDayClick]);

    // DayItem에 직접 클릭 이벤트 리스너 추가 (React 이벤트가 작동하지 않는 경우 대비)
    useEffect(() => {
        if (enableScroll || !onDayClick) return;

        const setupClickListeners = () => {
            if (!calendarRef.current) {
                setTimeout(setupClickListeners, 100);
                return;
            }

            const dayItems = calendarRef.current.querySelectorAll('[data-day-clickable="true"]');
            if (!dayItems || dayItems.length === 0) {
                setTimeout(setupClickListeners, 100);
                return;
            }

            const handlers = [];

            // 각 DayItem에 직접 이벤트 리스너 추가
            dayItems.forEach((item, index) => {
                const mouseDownData = { time: 0, x: 0, y: 0 };

                const mouseDownHandler = (e) => {
                    mouseDownData.time = Date.now();
                    mouseDownData.x = e.clientX;
                    mouseDownData.y = e.clientY;
                    // DayItem에서도 스와이프를 위해 isMouseDown 설정
                    if (enableWeekSwipe) {
                        swipeRef.current.isMouseDown = true;
                        swipeRef.current.startX = e.clientX;
                        swipeRef.current.startY = e.clientY;
                        swipeRef.current.endX = e.clientX;
                        swipeRef.current.endY = e.clientY;
                        swipeRef.current.hasMoved = false;
                        swipeRef.current.isClick = false;
                    }
                    // stopPropagation은 하지 않음 (스와이프 방해 방지)
                };

                const mouseUpHandler = (e) => {
                    if (mouseDownData.time > 0) {
                        const deltaTime = Date.now() - mouseDownData.time;
                        const deltaX = Math.abs(e.clientX - mouseDownData.x);
                        const deltaY = Math.abs(e.clientY - mouseDownData.y);

                        // 이동 거리가 작고 시간이 짧으면 클릭으로 간주
                        if (deltaX < 10 && deltaY < 10 && deltaTime < 500) {
                            e.stopPropagation();
                            const dateStr = item.getAttribute('data-date');
                            if (dateStr) {
                                const date = new Date(dateStr);
                                console.log('Parsed date:', date);
                                handleDayClick(date, index, e);
                            }
                            // 클릭인 경우 스와이프 처리 무시
                            if (enableWeekSwipe) {
                                swipeRef.current.isMouseDown = false;
                            }
                        } else if (enableWeekSwipe && swipeRef.current.isMouseDown) {
                            // 스와이프인 경우 handleWeekSwipeMouseUp 호출
                            swipeRef.current.endX = e.clientX;
                            swipeRef.current.endY = e.clientY;
                            swipeRef.current.hasMoved = true;
                            handleWeekSwipeMouseUp(e);
                        }
                        mouseDownData.time = 0;
                    }
                };

                const clickHandler = (e) => {
                    // 클릭 이벤트는 항상 처리 (mouseup이 작동하지 않는 경우 대비)
                    e.stopPropagation();
                    const dateStr = item.getAttribute('data-date');
                    if (dateStr) {
                        const date = new Date(dateStr);
                        handleDayClick(date, index, e);
                    }
                };

                // capture phase에서 처리하여 다른 이벤트보다 먼저 실행
                item.addEventListener('mousedown', mouseDownHandler, true);
                item.addEventListener('mouseup', mouseUpHandler, true);
                item.addEventListener('click', clickHandler, true);
                handlers.push({
                    item,
                    mouseDownHandler,
                    mouseUpHandler,
                    clickHandler
                });
            });

            return () => {
                handlers.forEach((handler) => {
                    if (handler.item) {
                        handler.item.removeEventListener('mousedown', handler.mouseDownHandler, true);
                        handler.item.removeEventListener('mouseup', handler.mouseUpHandler, true);
                        handler.item.removeEventListener('click', handler.clickHandler, true);
                    }
                });
            };
        };

        const cleanup = setupClickListeners();

        return () => {
            if (cleanup) cleanup();
        };
    }, [enableScroll, onDayClick, handleDayClick, days, enableWeekSwipe, handleWeekSwipeMouseUp]);

    // 스크롤이 비활성화된 경우 기본 렌더링 (주단위 스와이프 포함)
    if (!enableScroll) {
        return (
            <StyledWeekCalendar
                ref={calendarRef}
                sx={sx}
                size={size}
                enableScroll={enableScroll}
                enableWeekSwipe={enableWeekSwipe}
                onWheel={enableWeekSwipe ? handleWheel : undefined}
                style={enableWeekSwipe ? {
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent', // iOS에서 탭 하이라이트 제거
                    cursor: 'grab', // 마우스 커서 변경
                } : undefined}
                onMouseDown={(e) => {
                    // DayItem에서 발생한 마우스 다운은 무시 (드래그 방지)
                    const clickableElement = e.target.closest('[data-day-clickable="true"]');
                    if (clickableElement) {
                        console.log('DayItem detected in StyledWeekCalendar, skipping');
                        return; // DayItem에서 발생한 경우 드래그 처리하지 않음
                    }

                    // DayItem이 아닌 곳에서만 드래그 시작
                    handleWeekSwipeMouseDown(e);
                }}
                {...props}
            >
                {days.map((day, index) => (
                    <DayItem
                        key={index}
                        isSelected={day.isSelected}
                        size={size}
                        enableScroll={enableScroll}
                        enableWeekSwipe={enableWeekSwipe}
                        selectedBackgroundColor={selectedBackgroundColor}
                        data-day-clickable="true"
                        data-date={day.date ? day.date.toISOString() : ''}
                        sx={{
                            cursor: onDayClick ? 'pointer' : 'default',
                            pointerEvents: 'auto',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: day.isSelected
                                    ? theme.palette.figma.white[100]
                                    : theme.palette.figma.white[60],
                                fontWeight: day.isSelected ? 500 : 400,
                                letterSpacing: '-0.36px',
                                pointerEvents: 'none', // 텍스트는 클릭 불가, 부모로 전파
                            }}
                        >
                            {day.day}
                        </Typography>

                        <DayChart isSelected={day.isSelected} size={size}>
                            {day.chartData ? (
                                <DonutChart
                                    data={day.chartData}
                                    size={size === 'large' ? 80 : 40}
                                    centerRadius={size === 'large' ? 50 : 20}
                                />
                            ) : (
                                day.icon && day.icon
                            )}
                        </DayChart>

                        <Typography
                            sx={{
                                fontSize: '12px',
                                color: day.isSelected
                                    ? theme.palette.figma.white[100]
                                    : theme.palette.figma.white[60],
                                fontWeight: day.isSelected ? 500 : 400,
                                letterSpacing: '-0.36px',
                                pointerEvents: 'none', // 텍스트는 클릭 불가, 부모로 전파
                            }}
                        >
                            {day.date ? (day.isSelected ? formatDateTimeDisplay(day.date, 'monthDay') : day.date.getDate()) : ''}
                        </Typography>
                    </DayItem>
                ))}
            </StyledWeekCalendar>
        );
    }

    return (
        <CalendarContainer enableScroll={enableScroll} sx={{ ...sx }} {...props}>
            <StyledWeekCalendar
                ref={calendarRef}
                enableScroll={enableScroll}
                size={size}
                onScroll={handleScroll}
                onMouseDown={handleDragScrollMouseDown}
                sx={{
                    overflowX: 'auto',
                    overflowY: 'visible', // 선택된 날짜 그림자가 잘리지 않도록
                    cursor: isDragScrolling ? 'grabbing' : 'grab',
                    width: '100%',
                    maxWidth: fullWidth ? '100%' : (enableScroll ? (size === 'large' ? '700px' : '315px') : '700px'), // fullWidth면 화면 끝까지
                    scrollBehavior: 'auto',
                    position: 'relative',
                    alignSelf: 'center',
                    WebkitOverflowScrolling: 'touch', // iOS에서 부드러운 스크롤
                    touchAction: 'pan-x', // 수평 스크롤만 허용
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    scrollbarWidth: 'none', // Firefox에서 스크롤바 숨김
                    msOverflowStyle: 'none', // IE/Edge에서 스크롤바 숨김
                    '&::-webkit-scrollbar': {
                        display: 'none', // WebKit 브라우저에서 스크롤바 숨김
                    },
                }}
            >
                {isLoading ? (
                    // 로딩 상태 표시
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '80px',
                        color: theme.palette.figma.white[60]
                    }}>
                        <Typography sx={{ fontSize: '12px' }}>로딩 중...</Typography>
                    </Box>
                ) : (
                    <Box
                        data-inner-box
                        sx={{
                            display: 'flex',
                            width: (() => {
                                const dataLength = (allDaysData && allDaysData.length > 0 ? allDaysData.length : 14);
                                const itemWidth = size === 'large' ? 100 : 45;
                                const gap = (enableScroll && size === 'medium') ? 3 : 5;
                                // 정확한 너비 계산: 마지막 아이템 이후 gap이 없으므로 (dataLength - 1) * gap
                                // 우측 여백을 줄이기 위해 정확히 계산
                                return `${(dataLength * itemWidth) + ((dataLength - 1) * gap)}px`;
                            })(),
                            minWidth: (() => {
                                const dataLength = (allDaysData && allDaysData.length > 0 ? allDaysData.length : 14);
                                const itemWidth = size === 'large' ? 100 : 45;
                                const gap = (enableScroll && size === 'medium') ? 3 : 5;
                                return `${(dataLength * itemWidth) + ((dataLength - 1) * gap)}px`;
                            })(),
                            maxWidth: (() => {
                                const dataLength = (allDaysData && allDaysData.length > 0 ? allDaysData.length : 14);
                                const itemWidth = size === 'large' ? 100 : 45;
                                const gap = (enableScroll && size === 'medium') ? 3 : 5;
                                return `${(dataLength * itemWidth) + ((dataLength - 1) * gap)}px`; // 최대 너비도 동일하게 설정
                            })(),
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            flexShrink: 0,
                            overflow: 'visible', // 선택된 날짜 그림자가 잘리지 않도록
                            paddingRight: 0, // 우측 패딩 제거
                            paddingBottom: '4px', // 선택된 날짜 그림자가 영역 안에 들어가도록
                            marginRight: 0 // 우측 마진 제거
                        }}>
                        {(allDaysData && allDaysData.length > 0 ? allDaysData : []).map((day, dayIndex) => (
                            <DayItem
                                key={dayIndex}
                                data-day-item
                                isSelected={day.isSelected}
                                size={size}
                                enableScroll={enableScroll}
                                selectedBackgroundColor={selectedBackgroundColor}
                                onClick={(e) => {
                                    // 드래그로 스크롤한 뒤 발생하는 클릭은 무시
                                    if (dragScrollRef.current.moved) return;
                                    handleDayClick(day.date, dayIndex, e);
                                }}
                                sx={{ cursor: onDayClick ? 'pointer' : 'default' }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: day.isSelected
                                            ? theme.palette.figma.white[100]
                                            : theme.palette.figma.white[60],
                                        fontWeight: day.isSelected ? 500 : 400,
                                        letterSpacing: '-0.36px',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                    }}
                                >
                                    {day.day}
                                </Typography>

                                <DayChart isSelected={day.isSelected} size={size}>
                                    {day.chartData ? (
                                        <DonutChart
                                            data={day.chartData}
                                            size={size === 'large' ? 80 : 35}
                                            centerRadius={size === 'large' ? 50 : 15}
                                        />
                                    ) : (
                                        day.icon && day.icon
                                    )}
                                </DayChart>

                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: day.isSelected
                                            ? theme.palette.figma.white[100]
                                            : theme.palette.figma.white[60],
                                        fontWeight: day.isSelected ? 500 : 400,
                                        letterSpacing: '-0.36px',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none',
                                    }}
                                >
                                    {day.date ? (day.isSelected ? formatDateTimeDisplay(day.date, 'monthDay') : day.date.getDate()) : ''}
                                </Typography>
                            </DayItem>
                        ))}
                    </Box>
                )}
            </StyledWeekCalendar>
        </CalendarContainer>
    );
};

export default WeekCalendar;
